import { CommonModule } from '@angular/common';
import * as i0 from '@angular/core';
import { InjectionToken, Injectable, Inject, NgModule, Optional, SkipSelf } from '@angular/core';
import * as i1 from '@angular/common/http';
import { map, switchMap } from 'rxjs/operators';
import { of } from 'rxjs';

const CommonBackendConfigToken = new InjectionToken('CommonBackendConfig');

/**
 * @copyright
 * Copyright(c) 2021 SICK AG
 */
class CommonDashboardsService {
    constructor(http, commonBackendConfig) {
        this.http = http;
        this.commonBackendConfig = commonBackendConfig;
        this.url = `${commonBackendConfig.baseUrl}/api/v1/dashboards`;
    }
    static pickDefaultDashboard(dashboardsResponse) {
        const { dashboards, lastViewedId } = dashboardsResponse;
        if (dashboards.length === 0) {
            throw new Error('No dashboard available');
        }
        return (lastViewedId && dashboards.find((dashboard) => dashboard.dashboardId === lastViewedId)) ?? dashboards[0];
    }
    getDefaultDashboard() {
        return this.getDashboards().pipe(map(CommonDashboardsService.pickDefaultDashboard));
    }
    getDashboards() {
        return this.http.get(`${this.url}`);
    }
    getDashboard(dashboardId) {
        return this.http.get(`${this.url}/${dashboardId}`);
    }
    updateDashboard(dashboardId, dscDashboard) {
        return this.http.put(`${this.url}/${dashboardId}`, { name: dscDashboard.name });
    }
    createDashboard() {
        return this.http.post(`${this.url}`, '');
    }
    duplicateDashboard(dashboardId) {
        return this.http.post(`${this.url}?source=${dashboardId}`, '');
    }
    deleteDashboard(dashboardId) {
        return this.http.delete(`${this.url}/${dashboardId}`);
    }
}
CommonDashboardsService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.0.0", ngImport: i0, type: CommonDashboardsService, deps: [{ token: i1.HttpClient }, { token: CommonBackendConfigToken }], target: i0.ɵɵFactoryTarget.Injectable });
CommonDashboardsService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.0.0", ngImport: i0, type: CommonDashboardsService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.0.0", ngImport: i0, type: CommonDashboardsService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.HttpClient }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [CommonBackendConfigToken]
                }] }]; } });

class DashboardBuilderBackendApiModule {
    constructor(parentModule) {
        if (parentModule) {
            throw new Error('DashboardBuilderBackendApiModule is already loaded. Import it in the AppModule only');
        }
    }
    static forRoot(config = { baseUrl: '' }) {
        const internalConfig = { ...config };
        // Chop off trailing / from URL
        internalConfig.baseUrl = internalConfig.baseUrl.replace(/\/+$/, '');
        return {
            ngModule: DashboardBuilderBackendApiModule,
            providers: [{ provide: CommonBackendConfigToken, useValue: internalConfig }, CommonDashboardsService],
        };
    }
}
DashboardBuilderBackendApiModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.0.0", ngImport: i0, type: DashboardBuilderBackendApiModule, deps: [{ token: DashboardBuilderBackendApiModule, optional: true, skipSelf: true }], target: i0.ɵɵFactoryTarget.NgModule });
DashboardBuilderBackendApiModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.0.0", ngImport: i0, type: DashboardBuilderBackendApiModule, imports: [CommonModule] });
DashboardBuilderBackendApiModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.0.0", ngImport: i0, type: DashboardBuilderBackendApiModule, imports: [CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.0.0", ngImport: i0, type: DashboardBuilderBackendApiModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule],
                }]
        }], ctorParameters: function () { return [{ type: DashboardBuilderBackendApiModule, decorators: [{
                    type: Optional
                }, {
                    type: SkipSelf
                }] }]; } });

/**
 * @copyright
 * Copyright(c) 2021 SICK AG
 */
class CommonDashboardContentService {
    constructor(http, commonBackendConfig) {
        this.http = http;
        this.commonBackendConfig = commonBackendConfig;
        this.url = `${commonBackendConfig.baseUrl}/api/v1/dashboards`;
    }
    getContent(dashboardId) {
        return this.http
            .get(`${this.url}/${dashboardId}/content`)
            .pipe(switchMap((resp) => of(resp.dashboard)));
    }
    updateContent(dashboardId, rootNode) {
        return this.http.put(`${this.url}/${dashboardId}/content`, rootNode).pipe(switchMap(() => of(rootNode)));
    }
}
CommonDashboardContentService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.0.0", ngImport: i0, type: CommonDashboardContentService, deps: [{ token: i1.HttpClient }, { token: CommonBackendConfigToken }], target: i0.ɵɵFactoryTarget.Injectable });
CommonDashboardContentService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.0.0", ngImport: i0, type: CommonDashboardContentService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.0.0", ngImport: i0, type: CommonDashboardContentService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.HttpClient }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [CommonBackendConfigToken]
                }] }]; } });

/**
 * @copyright
 * Copyright(c) 2021 SICK AG
 */
class CommonDashboardSettingsService {
    constructor(http, commonBackendConfig) {
        this.http = http;
        this.commonBackendConfig = commonBackendConfig;
        this.url = `${commonBackendConfig.baseUrl}/api/v1/dashboards`;
    }
    getSettings(dashboardId) {
        return this.http
            .get(`${this.url}/${dashboardId}/settings`)
            .pipe(switchMap((resp) => of(resp.settings)));
    }
    updateSettings(dashboardId, settings) {
        return this.http
            .put(`${this.url}/${dashboardId}/settings`, settings)
            .pipe(switchMap((resp) => of(resp.settings)));
    }
}
CommonDashboardSettingsService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.0.0", ngImport: i0, type: CommonDashboardSettingsService, deps: [{ token: i1.HttpClient }, { token: CommonBackendConfigToken }], target: i0.ɵɵFactoryTarget.Injectable });
CommonDashboardSettingsService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.0.0", ngImport: i0, type: CommonDashboardSettingsService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.0.0", ngImport: i0, type: CommonDashboardSettingsService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.HttpClient }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [CommonBackendConfigToken]
                }] }]; } });

/**
 * @copyright
 * Copyright(c) 2021 SICK AG
 */
class CommonDashboardShareService {
    constructor(http, commonBackendConfig) {
        this.http = http;
        this.commonBackendConfig = commonBackendConfig;
        this.url = `${commonBackendConfig.baseUrl}/api/v1/dashboards`;
    }
    getShareList(dashboardId) {
        return this.http
            .get(`${this.url}/${dashboardId}/share`)
            .pipe(map((response) => response.shareList));
    }
    createShareEntry(dashboardId, userEmail, permission) {
        return this.http.post(`${this.url}/${dashboardId}/share`, {
            userEmail,
            permission,
        });
    }
    updateShareEntry(dashboardId, userEmail, permission) {
        return this.http.put(`${this.url}/${dashboardId}/share/${encodeURIComponent(userEmail)}`, {
            permission,
        });
    }
    deleteShareEntry(dashboardId, userEmail) {
        return this.http.delete(`${this.url}/${dashboardId}/share/${encodeURIComponent(userEmail)}`);
    }
}
CommonDashboardShareService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.0.0", ngImport: i0, type: CommonDashboardShareService, deps: [{ token: i1.HttpClient }, { token: CommonBackendConfigToken }], target: i0.ɵɵFactoryTarget.Injectable });
CommonDashboardShareService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.0.0", ngImport: i0, type: CommonDashboardShareService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.0.0", ngImport: i0, type: CommonDashboardShareService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.HttpClient }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [CommonBackendConfigToken]
                }] }]; } });

/**
 * Generated bundle index. Do not edit.
 */

export { CommonBackendConfigToken, CommonDashboardContentService, CommonDashboardSettingsService, CommonDashboardShareService, CommonDashboardsService, DashboardBuilderBackendApiModule };
